import { oc } from "@orpc/contract";
import { openapi } from "@orpc/openapi";
import { z } from "zod";
import { publicIdSchema } from "./public-id";

// v3.2 review layer (stage 2): where each note sentence came from in the
// transcript ("Sources"), and the AI flags on the transcript. Both are read
// models derived from the stored note, transcripts, timed segments and the
// worker's flags; neither changes the note or the transcript.

const errors = (unavailable: string) => ({
  BAD_REQUEST: { message: "Invalid request" },
  UNAUTHORIZED: { message: "Authentication required" },
  FORBIDDEN: { message: "Account required" },
  NOT_FOUND: { message: "Visit not found" },
  METHOD_NOT_ALLOWED: { message: "Method Not Allowed" },
  INTERNAL_SERVER_ERROR: { message: "Internal Server Error" },
  SERVICE_UNAVAILABLE: { message: unavailable },
});
const input = z.object({
  params: z.strictObject({ public_id: publicIdSchema }),
  query: z.strictObject({}).default({}),
  body: z.undefined().optional(),
});
const meta = (path: `/${string}`) =>
  openapi({
    method: "GET",
    path,
    inputStructure: "detailed",
    successStatus: 200,
    spec: (spec) => ({ ...spec, security: [{ bearerAuth: [] }] }),
  });
const audioSchema = z.object({
  available: z.boolean(),
  url: z.string().startsWith("/api/v2/visits/").nullable(),
});

// --- Sources for every line ---

export const NOTE_SOURCE_ISSUES = [
  "number_not_found",
  "number_mismatch",
  "negation_mismatch",
  "drug_not_found",
  "no_support",
  "verifier_rejected",
] as const;
// Sentences returned per note; a longer note is cut and `truncated` set.
export const NOTE_SOURCES_MAX_SENTENCES = 400;
export const noteSourceSegmentSchema = z.object({
  // Index into `transcripts`.
  transcript: z.int().min(0),
  idx: z.int().min(0),
  // Null when the transcript has no timings (realtime, translated, legacy):
  // the words are shown without "Replay this moment".
  startMs: z.int().min(0).nullable(),
  endMs: z.int().min(0).nullable(),
  speaker: z.int().min(0).max(99).nullable(),
  text: z.string(),
});
export const noteSourceSentenceSchema = z.object({
  // 0-based line of the note (note.split("\n")) and sentence in that line.
  line: z.int().min(0),
  sentence: z.int().min(0),
  text: z.string(),
  // 0..1; `supported` is the citation threshold applied.
  confidence: z.number().min(0).max(1),
  supported: z.boolean(),
  issues: z.array(z.enum(NOTE_SOURCE_ISSUES)),
  // Supporting segments; for an unsupported sentence with a disagreement
  // (a number or negation that differs), the closest place to check.
  segments: z.array(noteSourceSegmentSchema).max(3),
});
export const visitNoteSourcesSchema = z.object({
  // SHA-256 of the main note the sentences were taken from.
  noteVersion: z.string().regex(/^[0-9a-f]{64}$/),
  transcripts: z.array(
    z.object({
      recordingPublicId: publicIdSchema.nullable(),
      timed: z.boolean(),
      audio: audioSchema,
    }),
  ),
  sentences: z.array(noteSourceSentenceSchema),
  truncated: z.boolean(),
});
export type VisitNoteSources = z.output<typeof visitNoteSourcesSchema>;
export type NoteSourceSentence = z.output<typeof noteSourceSentenceSchema>;
export type NoteSourceSegment = z.output<typeof noteSourceSegmentSchema>;

// --- AI flags on the transcript ---

export const TRANSCRIPT_FLAG_SEVERITIES = ["red", "amber", "blue"] as const;
export const TRANSCRIPT_FLAG_CATEGORIES = [
  "dose",
  "allergy",
  "vital",
  "medication",
  "laterality",
  "negation",
  "contradiction",
  "other_risk",
  "unclear_audio",
  "low_confidence",
  "auto_corrected",
] as const;
export const TRANSCRIPT_FLAG_STATES = [
  // Reviewed; `flags` may be empty.
  "ready",
  // Not reviewed (feature off when the note was written, review failed,
  // older visit, still processing).
  "none",
] as const;
export const transcriptFlagSchema = z.object({
  // Stable within one response: "<transcript>-<n>".
  key: z.string().max(16),
  severity: z.enum(TRANSCRIPT_FLAG_SEVERITIES),
  category: z.enum(TRANSCRIPT_FLAG_CATEGORIES),
  quote: z.string(),
  // [start, end) in UTF-16 code units of the transcript text as the visit
  // currently returns it.
  start: z.int().min(0),
  end: z.int().min(0),
  reason: z.string(),
  suggestion: z.string().nullable(),
});
export type TranscriptFlagItem = z.output<typeof transcriptFlagSchema>;
export const visitTranscriptFlagsSchema = z.object({
  transcripts: z.array(
    z.object({
      recordingPublicId: publicIdSchema.nullable(),
      state: z.enum(TRANSCRIPT_FLAG_STATES),
      flags: z.array(transcriptFlagSchema),
    }),
  ),
  counts: z.object({
    red: z.int().min(0),
    amber: z.int().min(0),
    blue: z.int().min(0),
  }),
});
export type VisitTranscriptFlags = z.output<typeof visitTranscriptFlagsSchema>;

export const visitReviewContract = {
  getVisitNoteSources: oc
    .errors(errors("Note sources unavailable"))
    .input(input)
    .meta(meta("/api/v2/visits/{public_id}/note-sources"))
    .output(visitNoteSourcesSchema),
  getVisitTranscriptFlags: oc
    .errors(errors("Transcript flags unavailable"))
    .input(input)
    .meta(meta("/api/v2/visits/{public_id}/transcript-flags"))
    .output(visitTranscriptFlagsSchema),
};
